import { quantile } from 'd3-array'
import { ceil, floor, round } from 'lodash'

import { DARK_BLUE, DARK_GREEN, IPS_AQUA, IPS_YELLOW, MIDDLE_GREEN } from '@/common/constants'
import { getArrayBounds, roundPreciseNumber } from '@/common/utils/numbers'

export interface MapColorScale {
  readonly bins: ColorBin[]
  getMapColor: (value: number) => string
  readonly mapboxStyleStepBins: (string | number)[]
}

export interface ColorBin {
  value: number | string
  displayMin?: number | string
  displayMax?: number | string
  /** Decimal places this bin's labels are written at. Undefined means whole numbers. */
  decimals?: number
  color: string
}

// Some Color Schemes come from https://colorbrewer2.org
export const COLOR_SCHEMES: { [key: string]: string[] } = {
  BLUEBERRY_LEMONADE_BLUE: ['#FFFFD9', '#CAEAB4', '#A3D0BD', '#2C9CC1', '#2D819C'],
  YELLOW_TO_BLUE_7_BINS: [
    '#FFEDAB',
    '#c7e9b4',
    '#7fcdbb',
    '#41b6c4',
    '#1d91c0',
    '#225ea8',
    '#0c2c84',
  ],
  YELLOW_TO_RED_7_BINS: [
    '#FEF0D9',
    '#FDD49E',
    '#FDBB84',
    '#FC8D59',
    '#EF6548',
    '#D7301F',
    '#990000',
  ],
  BEE_HIVE_5_BINS: ['#FFF3B4', '#FFAB5E', '#FF6683', '#6326C5', '#130060'],
  BEE_HIVE_7_BINS: ['#FFF3B4', '#FFD361', '#FFAB5E', '#FF6683', '#D01ED3', '#6326C5', '#130060'],
  BLUE_TO_RED_6_BINS: ['#4575b4', '#91bfdb', '#e0f3f8', '#fee090', '#fc8d59', '#d73027'],
  BLUE_GREEN_YELLOW_3_BINS: [IPS_AQUA, MIDDLE_GREEN, IPS_YELLOW],
  IPS_YELLOW_TO_BLUE_7_BINS: [
    '#FFD43F',
    '#A8D87B',
    MIDDLE_GREEN,
    '#33C7BC',
    '#22BEDE',
    '#22AFD6',
    DARK_BLUE,
  ],
  GREEN_GRADIENT: ['#CFFFEF', '#C0F2E9', '#A5F2E5', MIDDLE_GREEN, '#4BD8B3', '#33B7B1', DARK_GREEN],
  BLUE_GRADIENT: ['#C8EFF7', '#92DFF1', '#5DCFEA', IPS_AQUA, '#22AFD6', '#149FD6', DARK_BLUE],
}

const ZERO_BIN = { value: 0.00001, color: 'grey' }

export const DEFAULT_COLOR = '#fff'

/**
 * Labels never carry more precision than the data behind them: vehicle counts reach the
 * front end already rounded to two decimals, and past that the digits are noise.
 */
export const DEFAULT_MAX_LABEL_DECIMALS = 2

/** How every edge of one bin ladder is written. `significant` is the roundest of the two. */
interface LadderPrecision {
  decimals: number
  significant: boolean
}

/**
 * Round one edge of a bin ladder for display.
 *
 * Every edge goes through this same call, so a bin's upper label is exactly the next bin's
 * lower label. Rounding each bin at a precision derived from its own width does not have
 * that property - it turns one quantile into `0.507` on one row and `0.5` on the next,
 * which reads as a gap between them.
 */
const roundLadderEdge = (edge: number, { decimals, significant }: LadderPrecision): number =>
  significant && Math.abs(edge) >= 1
    ? round(roundPreciseNumber(edge, 2), decimals)
    : round(edge, decimals)

/**
 * The roundest way of writing a ladder that still tells every edge of it apart.
 *
 * Precision is spent only where it buys something, in order: two significant digits (what
 * the legend has always shown for large breaks - `52447` -> `52000`), then exact whole
 * numbers when that is what separates two breaks a thousand apart, then decimals, capped at
 * `maxDecimals`. Integer metrics therefore keep integer labels even though the quantile
 * breaks between them are fractional, and decimals appear only where whole numbers would
 * print the same label twice - the sub-1 vehicle averages that made every bin on the
 * distribution map read `0`.
 */
const getLadderPrecision = (edges: number[], maxDecimals: number): LadderPrecision => {
  const candidates: LadderPrecision[] = [
    { decimals: 0, significant: true },
    { decimals: 0, significant: false },
    ...Array.from({ length: maxDecimals }, (_, index) => ({
      decimals: index + 1,
      significant: false,
    })),
  ]

  const distinct = candidates.find(candidate => {
    const labels = edges.map(edge => roundLadderEdge(edge, candidate))
    return new Set(labels).size === labels.length
  })

  return distinct ?? candidates[candidates.length - 1]
}

/**
 * Merge bins whose upper bound would print the same label as the bin before them, keeping the
 * last of each run - that bin's edge is the run's real upper bound and its color is the one
 * the top of the range already paints. Ties survive the whole precision ladder when the data
 * is flat enough, which is why this runs after the precision is chosen rather than instead
 * of it.
 */
const collapseLadderBins = (bins: ColorBin[], precision: LadderPrecision): ColorBin[] =>
  bins.reduce<ColorBin[]>((survivors, bin) => {
    const previous = survivors[survivors.length - 1]
    if (
      survivors.length &&
      roundLadderEdge(Number(bin.value), precision) ===
        roundLadderEdge(Number(previous.value), precision)
    )
      survivors[survivors.length - 1] = bin
    else survivors.push(bin)
    return survivors
  }, [])

const getBinsForSmallValueSet = (
  colors: string[],
  values: number[],
  maxDecimals: number = DEFAULT_MAX_LABEL_DECIMALS
): ColorBin[] => {
  const uniqueValues = [...new Set(values)].sort((a, b) => a - b)
  const binColors = []
  const numberOfColorsToUse = uniqueValues.length
  // if the numberOfColorsToUse is 1 and that value is falsy,  use the default color
  if (numberOfColorsToUse == 1 && !uniqueValues[0]) binColors.push(DEFAULT_COLOR)
  else {
    binColors.push(
      ...colors
        .filter(
          (_, index) => index % Math.floor((colors.length - 1) / (numberOfColorsToUse - 1)) === 0
        )
        .slice(0, numberOfColorsToUse - 1)
    )

    binColors.push(colors[colors.length - 1])
  }

  // These bins are labelled with the raw value rather than a range, so the precision has to
  // separate the values themselves.
  const { decimals } = getLadderPrecision(uniqueValues, maxDecimals)

  const bins = binColors.map((color, binIndex) => {
    return {
      value: uniqueValues[binIndex],
      decimals,
      color,
    }
  })

  // A capped precision can leave two values sharing a label - `0.25` and `0.4` are both `0` at
  // `maxDecimals: 0`. The legend writes these rows straight from `value`, without the
  // significant-digit rounding a range ladder gets, so the merge reads them the same way.
  return collapseLadderBins(bins, { decimals, significant: false })
}

export const smallValueSetBinsRequired = (items: any[], maxCount: number) => {
  const uniqueValues = new Set()
  let i = 0
  while (i < items.length) {
    uniqueValues.add(items[i++])
    if (uniqueValues.size > maxCount) return false
  }
  return true
}

/**
 * Get bins that are equally distributed, any rounding to make binSize(0)...binSize(n-1) equal will be added to the last bin such that the binSize(0)...binSize(n-1) != binSize(n)
 * @param colors
 * @param values
 * @returns
 */
export const getBinsEqualDistribution = (
  colors: string[],
  values: number[],
  decimalPrecision?: number,
  allowSmallValueSetBins: boolean = true
): ColorBin[] => {
  const binCount = colors.length

  if (smallValueSetBinsRequired(values, colors.length) && allowSmallValueSetBins)
    return getBinsForSmallValueSet(colors, values)

  const [min, max] = getArrayBounds(values)
  const valuesRange = max - min
  const binValueDecimalPrecision =
    decimalPrecision ?? getDecimalPrecisionEqualDistribution(values, binCount)
  const binSize = floor(valuesRange / binCount, binValueDecimalPrecision)

  return colors.map((_, binIndex) => {
    const isFirstBin = binIndex === 0
    let binMin
    if (isFirstBin) {
      if (min < 0.1) {
        binMin = round(min, 2)
      } else if (min < 1) {
        binMin = round(min, 1)
      } else {
        binMin = round(min, Math.max(binValueDecimalPrecision, 0))
      }
    } else {
      binMin = round(min + binSize * binIndex, binValueDecimalPrecision)
    }

    const isLastBin = binIndex === binCount - 1
    const binMax = isLastBin
      ? ceil(max, Math.max(binValueDecimalPrecision, 0))
      : round(min + binSize * (binIndex + 1), binValueDecimalPrecision)

    return {
      value: binMax,
      displayMin: binMin,
      displayMax: binMax,
      decimals: Math.max(binValueDecimalPrecision, 0),
      color: colors[binIndex],
    }
  })
}

/**
 * The decimal precision (e.g. used in `round()` or `floor()`) needed to display these values in a readable manner in the map legend
 * @param values array of values that will be displayed
 * @param binCount the number of color bins to break the values into
 */
export const getDecimalPrecisionEqualDistribution = (
  values: number[],
  binCount: number
): number => {
  const [min, max] = getArrayBounds(values)
  const range = max - min

  // Threshold for rounding to nearest hundred
  if (range > binCount * 100) return -2

  // Threshold for rounding to nearest tens
  if (range > binCount * 10) return -1

  // Threshold for rounding to nearest integer
  if (range > binCount * 1) return 0

  // Threshold for rounding to nearest tenth
  if (range > binCount * 0.1) return 1

  // Default to round to nearest hundredth
  return 2
}

/**
 * Index of the bin a value paints with: the first bin whose upper edge is not less
 * than the value. Mirrors the Mapbox `step` expression built by
 * `generateMapboxStyleStepBins`, so bin membership and rendered color can't drift.
 */
const findBinIndex = (bins: ColorBin[], value: number): number => {
  let binIndex = 0
  while (binIndex < bins.length - 1 && value > Number(bins[binIndex].value)) {
    binIndex++
  }
  return binIndex
}

/**
 * Drop bins that no value falls into.
 *
 * Quantile breaks are interpolated, so on discrete data (e.g. integer event counts) a
 * bin can span a range that contains no possible value - `(1, 1.57]` holds no integer.
 * That bin's color is listed in the legend but never painted on the map. Surviving
 * bins keep their own color, so removing the empty ones leaves the map unchanged.
 */
const dropEmptyBins = (bins: ColorBin[], values: number[]): ColorBin[] => {
  const populatedBinIndexes = new Set(values.map(value => findBinIndex(bins, value)))
  const populatedBins = bins.filter((_, binIndex) => populatedBinIndexes.has(binIndex))
  return populatedBins.length ? populatedBins : bins
}

const getBinsQuantileDistribution = (
  colors: string[],
  values: number[],
  maxDecimals: number = DEFAULT_MAX_LABEL_DECIMALS
): ColorBin[] => {
  if (!values.length) {
    return [
      {
        value: NaN,
        color: DEFAULT_COLOR,
      },
    ]
  }

  // When number of unique values is smaller than the number of colors, use getBinsForSmallValueSet
  if (smallValueSetBinsRequired(values, colors.length))
    return getBinsForSmallValueSet(colors, values, maxDecimals)

  const binCount = colors.length

  const [min, max] = getArrayBounds(values)
  const quantiles = colors.map((_, index) => quantile(values, (index + 1) / binCount)) as number[]

  // If there are fewer unique bins than colors, it means a bin value is repeated,
  // so we will use linear binning to display the data
  if (new Set(quantiles).size < binCount) {
    return dropEmptyBins(getBinsEqualDistribution(colors, values), values)
  }

  // Every quantile keeps its row, including one whose range no value can land in: the legend
  // is read as a colour ramp, so a break is worth showing whether or not a shape sits in it.
  // The cost is that such a break still votes on precision below, which is why an integer
  // metric can end up labelled with decimals.
  const stops = quantiles.map((value, binIndex) => ({ value, color: colors[binIndex] }))

  // A bin's own min and max are two neighbouring stops, so no two rows can disagree about the
  // break between them. `min` floors the ladder without being a stop of its own - a first bin
  // reading `1 - 1` holds exactly the value 1, and is not a duplicate row.
  const precision = getLadderPrecision(
    stops.map(bin => Number(bin.value)),
    maxDecimals
  )

  const bins = collapseLadderBins(stops, precision).map((bin, binIndex, ladder) => {
    const binMax = Number(bin.value)

    return {
      value: binMax,
      displayMin: roundLadderEdge(
        binIndex === 0 ? min : Number(ladder[binIndex - 1].value),
        precision
      ),
      displayMax: roundLadderEdge(binMax, precision),
      decimals: precision.decimals,
      color: binMax ? bin.color : DEFAULT_COLOR,
    }
  })

  // `min` is the one edge the collapse above cannot merge, so the first bin is the one place a
  // row like `0 - 0` can still appear: a real range the chosen precision cannot write. Fold it
  // into the bin above instead of printing a row that says nothing. A first bin whose min and
  // max are the same *value* - every shape in it counted exactly 1 - is saying something, and
  // is left alone.
  const [first] = bins
  const isUnwritableRange = first.displayMin === first.displayMax && min !== Number(first.value)

  return bins.length > 1 && isUnwritableRange ? bins.slice(1) : bins
}

const FLOAT_BITS = new DataView(new ArrayBuffer(8))

/**
 * The smallest step stop that still keeps `value` itself inside the bin it closes:
 * the next representable double toward positive infinity.
 *
 * Mapbox `step` stops are inclusive lower bounds, so a bin's stop has to sit just
 * *above* its upper edge whatever the sign. Arithmetic nudges don't do that reliably
 * - scaling by `1 + EPSILON` moves *away from zero*, so on a negative edge it lands
 * below the edge and hands the edge value to the next color while `findBinIndex`
 * keeps it in this one, and near zero `|value| * EPSILON` underflows to nothing.
 * Stepping the IEEE-754 bit pattern is exact for every input instead.
 */
const nextStopAbove = (value: number): number => {
  if (Number.isNaN(value) || value === Infinity) return value
  // -0 compares equal to 0, and the smallest subnormal is the next value above both
  if (value === 0) return Number.MIN_VALUE

  FLOAT_BITS.setFloat64(0, value)
  // magnitude ascends with the bit pattern above zero and descends below it
  FLOAT_BITS.setBigUint64(0, FLOAT_BITS.getBigUint64(0) + (value > 0 ? 1n : -1n))

  return FLOAT_BITS.getFloat64(0)
}

export const generateMapboxStyleStepBins = (bins: ColorBin[]): (string | number)[] => {
  if (bins.length === 0 || !bins[0]?.color) return [DEFAULT_COLOR, 0, DEFAULT_COLOR]
  if (bins.length === 1) return [bins[0].color, 0, bins[0].color]
  return bins.reduce<(number | string)[]>((previousValues, { value, color }, idx) => {
    if (idx === bins.length - 1) return [...previousValues, color]

    const stop = nextStopAbove(value as number)
    // Mapbox requires strictly ascending `step` stops, so skip any bin that doesn't
    // advance past the previous one (e.g. a bin whose min == max)
    const previousStop = previousValues[previousValues.length - 1]
    if (typeof previousStop === 'number' && previousStop >= stop) return previousValues

    return [...previousValues, color, stop]
  }, [])
}

export class DiscreteQuantileMapColorScale implements MapColorScale {
  private colors: string[]
  private values: number[]
  bins: ColorBin[]

  /**
   * @param options.maxLabelDecimals cap on legend label precision. Pass 0 to keep whole
   *   numbers whatever the data does - bins that then share a label are merged instead.
   */
  constructor(
    colors: string[],
    values: number[],
    options?: { bins?: ColorBin[]; maxLabelDecimals?: number }
  ) {
    if (!colors.length) throw new RangeError('Colors array must not be empty')
    this.colors = colors
    this.values = values
    this.bins =
      options?.bins ??
      getBinsQuantileDistribution(
        this.colors,
        this.values.filter(value => value !== 0),
        options?.maxLabelDecimals ?? DEFAULT_MAX_LABEL_DECIMALS
      )
  }

  /**
   * Decimal places these bins are labelled at. Popups and totals read it so every number on
   * the page is written at the precision its legend uses.
   */
  get decimals(): number {
    return Math.max(0, ...this.bins.map(bin => bin.decimals ?? 0))
  }

  get mapboxStyleStepBins(): (string | number)[] {
    return generateMapboxStyleStepBins(this.bins)
  }

  /**
   * Iterate through the bins until the value is greater than the bin max, returning the last bin where the value was less than or equal to the bin max
   * @param value the number value to map to a color
   * @returns color string
   */
  getMapColor(value?: number): string {
    if (!value) return DEFAULT_COLOR

    return this.bins[findBinIndex(this.bins, value)].color
  }
}

export const generateDivergingBins = (
  range: number,
  negativeColors: string[],
  positiveColors: string[]
) => {
  return [
    ...getBinsEqualDistribution(negativeColors, [-range, -0.01], 2, false),
    ZERO_BIN,
    ...getBinsEqualDistribution(positiveColors, [0.01, range], 2, false),
  ]
}
